<template>
  <div class="page-article tier tier-three">
    <div class="tier-content">
      <Tooltip
        content="Here you can preview and edit article content, along with various settings"
      />

      <Notification
        v-if="notification.active"
        :content="notification.content"
        :type="notification.type"/>

      <div class="site-article">
        <Tabs ref="articleTabs" :headings="['Article Content', 'Featured Image', 'Delete Article']">
          <Tab>
              <EditArticleForm
                v-model="article"
                :websiteUrl="site.domain"
                :hasArticle="true"
                @resetTabHeight="resetTabHeight"
                @submit="submitArticle"
              />
          </Tab>

          <Tab>
            <div>
              <FileUploadForm
                label="Featured Image"
                helpText="File types accepted: <strong>JPG</strong>"
                paramResource="article"
                paramName="featured_image"
                :images="images"
                :stringUpdateFunction="stringUpdateFunction"
                :fileUpdateFunction="fileUpdateFunction"
              />
              <Btn v-if="images" secondary class="btn-remove" @click="deleteImage">
                Delete Image
              </Btn>
            </div>
          </Tab>

          <Tab>
            <div>
              <p>
                You are about to permanently delete the blog article
                <strong>{{ article.heading }}</strong
                >. If you would prefer to unpublish this, you can set the
                <strong>Publish At</strong> date to a previous date.
              </p>

              <hr />

              <Btn secondary @click="activateConfirm">
                Delete Article
              </Btn>
            </div>
          </Tab>
        </Tabs>
      </div>

      <Confirm
        v-if="confirmActive"
        @confirm="deleteArticle"
        @cancel="cancel"
        :message="deleteMessage"
        :link="{
          name: 'article',
          params: {
            companyId: this.companyId,
            siteId: this.siteId,
            articleId: this.articleId
          }
        }"
      />
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'

import page from '@/mixins/page'
import pagination from '@/mixins/pagination'
import confirm from '@/mixins/confirm'
import notification from '@/mixins/notification'

import {readCompanySiteData} from '@/lib/sites'
import {
  readCompanySiteArticleData,
  deleteCompanySiteArticleData,
  updateCompanySiteArticleData,
  updateCompanySiteArticleImage,
  deleteCompanySiteArticleImage
} from '@/lib/articles'

import EditArticleForm from '@/components/forms/EditArticleForm'
import FileUploadForm from '@/components/forms/FileUploadForm'
import Btn from '@/components/Btn'
import Tab from '@/components/Tab'
import Tabs from '@/components/Tabs'
import Tooltip from '@/components/Tooltip'

export default {
  name: 'Article',

  props: {
    companyId: {
      type: String,
      required: true
    },

    siteId: {
      type: String,
      required: true
    },

    articleId: {
      type: String,
      required: true
    }
  },

  mixins: [page, pagination, confirm, notification],

  components: {
    Btn,
    Tooltip,
    EditArticleForm,
    FileUploadForm,
    Tabs,
    Tab
  },

  data() {
    return {
      site: {},
      article: {},
      errors: {}
    }
  },

  computed: {
    ...mapGetters(['sessionToken']),

    title() {
      return `${this.article.heading}`
    },

    images() {
      return this.article.featured_image_versions
    },

    stringUpdateFunction() {
      const companyId = this.companyId
      const siteId = this.siteId
      const articleId = this.articleId

      return (article) => {
        return updateCompanySiteArticleData(
          companyId,
          siteId,
          articleId,
          article
        ).then(({data}) => {
          // TODO: duplicated below... prolly worth a tidy up

          // computed is currying callback function to be used by upload form
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.article = data

          this.triggerNotification('success', 'image', 'uploaded')

          this.$emit('updatePage')

          return data
        })
      }
    },

    fileUpdateFunction() {
      const companyId = this.companyId
      const siteId = this.siteId
      const token = this.sessionToken
      const articleId = this.articleId

      return (article) => {
        return updateCompanySiteArticleImage(
          companyId,
          siteId,
          articleId,
          article,
          token
        ).then(({data}) => {
          // computed is currying callback function to be used by upload form
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.article = data

          this.triggerNotification('success', 'image', 'uploaded')

          this.$emit('updatePage')

          return data
        })
      }
    },

    deleteMessage() {
      return `Are you sure you wish to delete the article, <strong class="name">${this.article.heading}</strong>?`
    }
  },

  methods: {
    getData() {
      readCompanySiteData(this.companyId, this.siteId).then(({data}) => {
        this.site = data
      })

      readCompanySiteArticleData(
        this.companyId,
        this.siteId,
        this.articleId
      ).then(({data}) => {
        this.article = data
      })
    },

    deleteImage() {
      deleteCompanySiteArticleImage(
        this.companyId,
        this.siteId,
        this.article.id
      ).then(() => {
        this.article.featured_image_versions = []
        this.$emit('updatePage')
        this.triggerNotification('success', 'image', 'deleted')
      })
    },

    resetTabHeight() {
      /**
       * When article content is updated, change the height of the tabs container
       */
      this.$refs.articleTabs.resetHeight()
    },

    submitArticle() {
      updateCompanySiteArticleData(
        this.companyId,
        this.siteId,
        this.articleId,
        this.article
      ).then(({data}) => {
        this.triggerNotification('success', 'article', 'updated')
        this.$emit('updatePage')
      })
    },

    deleteArticle() {
      deleteCompanySiteArticleData(
        this.companyId,
        this.siteId,
        this.articleId
      ).then(() => {
        this.confirmActive = false
        this.triggerNotification('success', 'article', 'deleted')
        this.$emit('updatePage')

        window.setTimeout(() => {
          this.$router.push({
            name: 'articles',
            params: {companyId: this.companyId, siteId: this.siteId}
          })
        }, this.closeDelay)
      })
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-article
  .btn-remove
    display: flex
    align-items: center
</style>
